import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { DateWrapper } from '@trucktrax/trucktrax-common';
import { PermissionAccess } from '@trucktrax/trucktrax-ts-common';
import { format, parseISO } from 'date-fns';
import React, { Component } from 'react';
import { connect } from 'react-redux';

import { DATASOURCE, DEVICES_PATH } from '../../../constants/apiConstants';
import { ADMIN_KEYS, ADMIN_LABELS, DEVICE_IDENTIFICATION } from '../../../constants/appConstants';
import { DEVICES_TEXT } from '../../../constants/navConstants';
import { setDataTableQueryParams } from '../../../store/actions/dataTableActions';
import {
  accessValue,
  addDataTest,
  addHeaderDataTest,
  getDeviceTypeLabel,
  getRegionUrlToNameMap,
  getUrl,
} from '../../../util/adminUtil';
import { getGeotraxBaseUrl } from '../../../util/apiUtil';
import DataTableContent from '../../shared/DataTableContent';
import DevicesAddModal from './DevicesAddModal';
import styles from './DevicesView.module.css';

export interface DevicesViewState {
  isOpen: boolean;
  isCloseRequest: boolean;
  displayTablet: boolean;
  displayGLinx: boolean;
}

export class DevicesView extends Component<DevicesViewProps, DevicesViewState> {
  static defaultProps = {
    regions: [],
    devicesPermissionAccess: '',
    tab: 0,
  };

  static getDisplayStateFromQueryParams = (queryParams?: any) => {
    const deviceType = queryParams?.deviceType;
    if (deviceType === DEVICE_IDENTIFICATION.TABLET) {
      return { displayTablet: true, displayGLinx: false };
    }
    if (deviceType === DEVICE_IDENTIFICATION.GLINX) {
      return { displayTablet: false, displayGLinx: true };
    }
    if (deviceType === 'none') {
      return { displayTablet: false, displayGLinx: false };
    }
    return null;
  };

  constructor(props: DevicesViewProps) {
    super(props);
    const displayState = DevicesView.getDisplayStateFromQueryParams(props.savedQueryParams);
    this.state = {
      isOpen: false,
      isCloseRequest: false,
      displayTablet: displayState?.displayTablet ?? true,
      displayGLinx: displayState?.displayGLinx ?? true,
    };
  }

  static getFormattedDate = (isoDateStr?: string) => {
    const validIsoDateString = '2024-01-01T00:00:00.000Z'; // placeholder value to prevent errors, will not be shown in the UI
    return format(parseISO(isoDateStr || validIsoDateString), 'MM/dd/yyyy HH:mm');
  };

  getColumns = () => {
    const columns = [
      {
        Header: addHeaderDataTest('header', 'sort-device-by-external-id', ADMIN_LABELS.EXTERNAL_ID),
        accessor: ADMIN_KEYS.EXTERNAL_ID,
        minWidth: 200,
        Cell: (props: { value: string }) =>
          addDataTest('cell', 'admin-device-external-id', props.value),
      },
      {
        Header: addHeaderDataTest('header', 'sort-device-by-type', ADMIN_LABELS.DEVICE_TYPE),
        accessor: ADMIN_KEYS.DEVICE_TYPE,
        minWidth: 100,
        Cell: (props: any) =>
          addDataTest('cell', 'admin-device-type', getDeviceTypeLabel(props.value)),
      },
      {
        Header: addHeaderDataTest(null, 'sort-device-by-name', ADMIN_LABELS.NAME),
        accessor: ADMIN_KEYS.NAME,
        minWidth: 150,
        Cell: (props: any) => addDataTest(props, 'admin-device-name', props.value),
      },
      {
        Header: addHeaderDataTest(null, 'sort-device-by-LASTVEHICLE', ADMIN_LABELS.LASTVEHICLE),
        id: ADMIN_KEYS.TRUCK_ALIAS,
        accessor: (row: any) => accessValue(ADMIN_KEYS.LAST_VEHICLE, row),
        sortable: true,
        minWidth: 150,
        Cell: (props: any) => addDataTest(props, 'admin-device-LASTVEHICLE', props.value),
      },
      {
        Header: addHeaderDataTest(null, 'sort-device-by-timeStamp', ADMIN_LABELS.VEHICLE_DATETIME),
        id: ADMIN_KEYS.TIMESTAMP,
        accessor: ADMIN_KEYS.UPDATED_TIMESTAMP,
        minWidth: 150,
        Cell: (props: any) =>
          addDataTest(props, 'admin-device-timeStamp', DevicesView.getFormattedDate(props.value)),
      },
      {
        Header: ADMIN_LABELS.REGION,
        id: ADMIN_KEYS.REGION,
        accessor: (row: any) => getRegionUrlToNameMap(this.props.regions)[getUrl(row)!],
        minWidth: 150,
        sortable: false,
      },
    ];

    const phoneCol = {
      Header: addHeaderDataTest(null, 'sort-device-by-phone', ADMIN_LABELS.PHONE),
      id: 'phoneNumber',
      accessor: (row: any) => {
        const phoneValue = accessValue(ADMIN_KEYS.PHONE, row);
        return phoneValue || 'N/A';
      },
      minWidth: 150,
      Cell: (props: any) => addDataTest(props, 'admin-device-phone', props.value),
    };

    if (this.props.tab === 1) {
      const deactivatedDateCol = {
        Header: addHeaderDataTest(
          null,
          'sort-device-by-deactivated-date',
          ADMIN_LABELS.DEACTIVATED_DATE,
        ),
        id: ADMIN_KEYS.ARCHIVED_TIMESTAMP,
        accessor: ADMIN_KEYS.ARCHIVED_TIMESTAMP,
        minWidth: 110,
        Cell: (props: { value: string }) =>
          addDataTest(
            null,
            'admin-device-deactivated-date',
            new DateWrapper(props.value).toUSDateString(),
          ),
      };
      return [...columns, deactivatedDateCol];
    }

    return [...columns, phoneCol];
  };

  requestCloseModal = () => {
    this.setState({
      isOpen: true,
      isCloseRequest: true,
    });
  };

  closeModal = () => {
    this.setState({
      isOpen: false,
      isCloseRequest: false,
    });
  };

  openModal = () => {
    this.setState({
      isOpen: true,
      isCloseRequest: false,
    });
  };

  handleTabChange = (_event: any, newValue: number) => {
    if (newValue === 1) {
      this.props.history.push('/admin/devices/deactivated');
    } else if (newValue === 0) {
      this.props.history.push('/admin/devices');
    }
  };

  handleDisplayCheckChange = (event: any) => {
    const { displayTablet, displayGLinx } = this.state;
    const { target } = event;
    const { name, checked } = target;
    const reverseGlinxValue = name === 'displayGLinx' && !checked && !displayTablet;
    const reverseTabletValue = name === 'displayTablet' && !checked && !displayGLinx;

    if (reverseGlinxValue) {
      this.setState(
        {
          displayGLinx: checked,
          displayTablet: true,
        },
        this.persistQueryParams,
      );
    } else if (reverseTabletValue) {
      this.setState(
        {
          displayGLinx: true,
          displayTablet: checked,
        },
        this.persistQueryParams,
      );
    } else if (name === 'displayTablet') {
      this.setState({ displayTablet: checked }, this.persistQueryParams);
    } else if (name === 'displayGLinx') {
      this.setState({ displayGLinx: checked }, this.persistQueryParams);
    }
  };

  getQueryParams = () => {
    const { displayTablet, displayGLinx } = this.state;

    if (displayTablet && displayGLinx) {
      return {};
    }
    if (!displayTablet && !displayGLinx) {
      return { deviceType: 'none' };
    }
    return {
      deviceType: `${displayTablet ? DEVICE_IDENTIFICATION.TABLET : DEVICE_IDENTIFICATION.GLINX}`,
    };
  };

  persistQueryParams = () => {
    this.props.setDataTableQueryParams(this.getQueryParams(), DATASOURCE.DEVICE);
  };

  render() {
    const columns = this.getColumns();
    const baseUrl = getGeotraxBaseUrl() + DEVICES_PATH;
    const hasPermissionAccess = this.props.devicesPermissionAccess === PermissionAccess.Edit;

    const tabs = (
      <Tabs
        value={this.props.tab}
        onChange={this.handleTabChange}
        classes={{
          root: styles.tabsContainer,
        }}
      >
        <Tab label="Active" data-test="devices-active-tab" />
        <Tab label="Deactivated" data-test="devices-deactivated-tab" />
      </Tabs>
    );

    const headerRightControls = (
      <>
        <span className={styles.checkboxTitle}>Device type:</span>
        <FormControlLabel
          data-test="device-type-filter-tablet"
          control={
            <Checkbox
              checked={this.state.displayTablet}
              onChange={this.handleDisplayCheckChange}
              name="displayTablet"
              color="primary"
            />
          }
          label={<span className={styles.checkboxLabel}>Tablets</span>}
          classes={{
            root: styles.checkboxLabelContainer,
          }}
        />
        <FormControlLabel
          data-test="device-type-filter-glinx"
          control={
            <Checkbox
              checked={this.state.displayGLinx}
              onChange={this.handleDisplayCheckChange}
              name="displayGLinx"
              color="primary"
            />
          }
          label={<span className={styles.checkboxLabel}>G-Linxes</span>}
          classes={{
            root: styles.checkboxLabelContainer,
          }}
        />
      </>
    );

    const props: any = {
      baseUrl,
      columns,
      renderTabs: tabs,
      headerRightControls,
      header: DEVICES_TEXT,
      isArchived: this.props.tab === 1,
      canAddNewItem: hasPermissionAccess,
      queryParams: this.getQueryParams(),
      bypassCache: true,
      source: DATASOURCE.DEVICE,
      enableExport: true,
      exportedFileName: 'devices',
    };
    return (
      <div className={styles.container}>
        <DataTableContent {...props} />
        {hasPermissionAccess && (
          <DevicesAddModal
            isOpen={this.state.isOpen}
            openModal={this.openModal}
            closeModal={this.requestCloseModal}
            regions={this.props.regions}
            discardAcceptAction={this.closeModal}
            discardRejectAction={this.openModal}
            isCloseRequest={this.state.isCloseRequest}
          />
        )}
      </div>
    );
  }
}

const mapStateToProps = (state: any) => ({
  regions: state.regionList,
  devicesPermissionAccess: state.adminPermissionAccess[DEVICES_TEXT],
  savedQueryParams: state.dataTableQueryParams?.[DATASOURCE.DEVICE]?.queryParams,
});

export interface History {
  push: (p: string) => void;
}

export interface DevicesViewProps {
  regions: any[];
  devicesPermissionAccess?: string;
  tab: number;
  history: History;
  savedQueryParams?: any;
  setDataTableQueryParams: (queryParams: any, source: string) => void;
}

export default connect(mapStateToProps, { setDataTableQueryParams })(DevicesView);
